import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Select,
  Stack,
  Text,
  Button,
  ActionIcon,
  Flex,
  RangeSlider,
  Checkbox,
  MultiSelect,
} from "@mantine/core";
import { GetCoursesParams, CourseFilterOptionsData } from "../../types/api";
import { getLanguageName } from "../../utils/helpers";
import { TrashCan } from "../../shared/svg/TrashCan";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import useCoursesStore from "../../store/useCoursesStore";

interface Props {
  credentials: GetCoursesParams;
  setCredentials: React.Dispatch<React.SetStateAction<GetCoursesParams>>;
  handleSearch: () => void;
  filterOptions: CourseFilterOptionsData;
  onClose: () => void;
}

export const FiltersPopoverContent: React.FC<Props> = ({
  credentials,
  setCredentials,
  handleSearch,
  filterOptions,
  onClose,
}) => {
  const [localFilters, setLocalFilters] =
    useState<GetCoursesParams>(credentials);
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentTab } = useCoursesStore();

  const selectedCategory = localFilters.category || null;

  const availableTopics = useMemo(() => {
    const foundCategory = filterOptions.categories_topics.find(
      (cat) => cat.category_name === selectedCategory
    );
    return foundCategory?.topics.map((t) => t.topic_name) || [];
  }, [selectedCategory, filterOptions.categories_topics]);

  const handleCategoryChange = (val: string | null) => {
    setLocalFilters((prev) => ({
      ...prev,
      category: val || undefined,
      topic: undefined,
    }));
  };

  const [shouldSearch, setShouldSearch] = useState(false);

  const applyFilters = () => {
    setCredentials(localFilters);
    setShouldSearch(true);
    onClose();

    const params = new URLSearchParams(location.search);
    const categoryParam = params.get("category");
    const topicParam = params.get("topic");

    if (categoryParam && !localFilters.category) {
      navigate("/courses");
    }
    if (topicParam && !localFilters.topic) {
      navigate("/courses");
    }
  };

  const resetFilters = () => {
    setLocalFilters({});
    setCredentials({});
    setShouldSearch(true);
    onClose();

    const params = new URLSearchParams(location.search);
    const categoryParam = params.get("category");

    if (categoryParam) {
      navigate("/courses");
    }
  };

  useEffect(() => {
    if (shouldSearch) {
      handleSearch();
      setShouldSearch(false);
    }
  }, [credentials, shouldSearch]);

  return (
    <Box>
      <Text fw={500} mb="sm">
        {t("filters.title")}
      </Text>
      <Stack gap="sm">
        <MultiSelect
          label={t("filters.platform")}
          placeholder={t("filters.select_platform")}
          data={Object.entries(filterOptions.platforms).map(([key, value]) => ({
            value: key,
            label: value,
          }))}
          value={localFilters.platform || []}
          onChange={(val) =>
            setLocalFilters((prev) => ({
              ...prev,
              platform: val.length ? val : undefined,
            }))
          }
          clearable
        />

        <Select
          label={t("filters.organization")}
          placeholder={t("filters.select_organization")}
          data={filterOptions.organizations.filter(
            (org) => typeof org === "string" && org.trim() !== ""
          )}
          value={localFilters.organization || null}
          onChange={(val) =>
            setLocalFilters((prev) => ({
              ...prev,
              organization: val || undefined,
            }))
          }
          clearable
        />
        <MultiSelect
          label={t("filters.language")}
          placeholder={t("filters.select_language")}
          data={filterOptions.languages
            .filter(
              (lan): lan is string =>
                typeof lan === "string" && lan.trim() !== ""
            )
            .map((lan) => ({
              value: lan.trim(),
              label: getLanguageName(lan.trim()),
            }))}
          value={localFilters.language || []}
          onChange={(val) =>
            setLocalFilters((prev) => ({
              ...prev,
              language: val.length ? val : undefined,
            }))
          }
          clearable
        />
        {currentTab === "My learning" && (
          <Select
            label={t("filters.status")}
            placeholder={t("filters.status")}
            data={Object.entries(filterOptions.statuses).map(
              ([key, value]) => ({
                value: key,
                label: t(key),
              })
            )}
            value={localFilters.status}
            onChange={(val) =>
              setLocalFilters((prev) => ({
                ...prev,
                status: val ? val : undefined,
              }))
            }
            clearable
          />
        )}

        <Select
          label={t("filters.category")}
          placeholder={t("filters.select_category")}
          data={filterOptions.categories_topics.map((cat) => cat.category_name)}
          value={localFilters.category || null}
          onChange={handleCategoryChange}
          clearable
        />

        {localFilters.category && (
          <Select
            label={t("filters.topic")}
            placeholder={t("filters.select_topic")}
            data={availableTopics.map(
              (topic) => topic.charAt(0).toUpperCase() + topic.slice(1)
            )}
            value={localFilters.topic || null}
            onChange={(val) =>
              setLocalFilters((prev) => ({ ...prev, topic: val || undefined }))
            }
            clearable
          />
        )}

        <div style={{ marginTop: 10 }}>
          <Text>{t("filters.price_range")}</Text>
          <RangeSlider
            min={0}
            max={200}
            step={5}
            value={[localFilters.price_min || 0, localFilters.price_max || 200]}
            onChange={(val) =>
              setLocalFilters((prev) => ({
                ...prev,
                price_min: val[0],
                price_max: val[1],
              }))
            }
            label={(value) => `${value}$`}
            marks={[
              { value: 0, label: "0$" },
              { value: 50, label: "50$" },
              { value: 100, label: "100$" },
              { value: 150, label: "150$" },
              { value: 200, label: "200$" },
            ]}
          />
        </div>

        <div style={{ marginTop: 15, marginBottom: 10 }}>
          <Text>{t("filters.rating_range")}</Text>
          <RangeSlider
            min={0}
            max={100}
            step={1}
            value={[
              (localFilters.rating_min ?? 0) * 20,
              (localFilters.rating_max ?? 5) * 20,
            ]}
            onChange={(val) =>
              setLocalFilters((prev) => ({
                ...prev,
                rating_min: val[0] / 20,
                rating_max: val[1] / 20,
              }))
            }
            label={(val) => (val / 20).toFixed(1)}
            marks={[
              { value: 0, label: "0" },
              { value: 100, label: "5" },
            ]}
          />
        </div>
        <Checkbox
          mt={10}
          label={t("filters.free_courses")}
          checked={localFilters.is_free || false}
          onChange={(event) =>
            setLocalFilters((prev) => ({
              ...prev,
              is_free: event.currentTarget.checked || undefined,
            }))
          }
        />

        <Flex align="center" w="100%" gap={10} mt="sm">
          <Button w="100%" onClick={applyFilters} color="violet">
            {t("filters.apply")}
          </Button>
          <ActionIcon variant="subtle" color="gray" onClick={resetFilters}>
            <TrashCan />
          </ActionIcon>
        </Flex>
      </Stack>
    </Box>
  );
};
